import { Standard } from '@esg/esg-global-types';
import { Box, Theme, useTheme, Grid } from '@mui/material';
import React from 'react';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { getStandards } from '../../../lib/app/standard';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import ConfigurationCard from '../../shared/cards/ConfigurationCard';
import { ReactComponent as EmissionFactorIcon } from '../../../assets/icons/Carbon Emissions.svg';
import { ReactComponent as CarbonTargetIcon } from '../../../assets/icons/Target.svg';
import AlertBanner from '../../shared/banner/AlertBanner';
import PrimarySkeleton from '../../shared/loading/PrimarySkeleton';

/**
 * Conditional navigation cards to configure additional data required by the configured company standards
 * @returns {JSX.Element}
 */
const PanelOtherConfiguration = () => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [standards, setStandards] = React.useState<Array<Standard>>([]);
  const [standardsLoading, setStandardsLoading] = React.useState<boolean>(true);
  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  const additional_config_standards: Array<Standard> = standards.filter((standard: Standard) => {
    return standard.require_emission_factor;
  });

  const fetchStandards = async (): Promise<void> => {
    setStandardsLoading(true);
    try {
      if (group && company) {
        const standards: Array<Standard> = await getStandards(group.id, company.id);
        setStandards(standards);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelOtherConfiguration failed on an unknown error while calling fetchStandards.',
          {
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch standards. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      return;
    } finally {
      setStandardsLoading(false);
    }
  };

  React.useEffect(() => {
    try {
      (async () => {
        setStandards([]);
        fetchStandards();
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: PanelOtherConfiguration failed on an unknown error while initialising.',
          {
            group: group,
            company: company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
    return;
  }, [group, company]);

  return (
    <Box>
      {standardsLoading ? (
        <PrimarySkeleton height={100} variant="rounded" />
      ) : additional_config_standards.length > 0 ? (
        <>
          {additional_config_standards.filter((standard: Standard) => {
            return standard.require_emission_factor;
          }).length > 0 && (
            <Grid container spacing={8}>
              <Grid size={4}>
                <ConfigurationCard
                  title="Emission Factors"
                  description="Convert your GHG Metrics to carbon emission data (CO2e)"
                  icon={
                    <EmissionFactorIcon height={35} fill={primary_blue} stroke={primary_blue} />
                  }
                  route="configuration/emission-factors"
                />
              </Grid>
              <Grid size={4}>
                <ConfigurationCard
                  title="Carbon Targets"
                  description="Set goals for your organisation's carbon emissions and track real time progress."
                  icon={<CarbonTargetIcon height={35} fill={primary_blue} stroke={primary_blue} />}
                  route="configuration/carbon-targets"
                />
              </Grid>
            </Grid>
          )}
        </>
      ) : (
        <AlertBanner
          severity="info"
          message={`No additional configuration required for ${company?.name}`}
          open
        />
      )}
    </Box>
  );
};

export default PanelOtherConfiguration;
